import { Activity, Search } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { Pagination } from '../components/Pagination';
import { StatusBadge } from '../components/StatusBadge';
import { api, dateTime } from '../lib/api';

const PAGE_SIZE = 30;

type AuditEvent = {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  outcome: string;
  occurredAt: string;
  admin: { fullName: string; email: string } | null;
};

export function AuditPage() {
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api<{ items: AuditEvent[]; total: number }>(
      `/audit?${new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        ...(query ? { action: query } : {}),
      })}`,
    )
      .then((result) => {
        setItems(result.items);
        setTotal(result.total);
      })
      .finally(() => setLoading(false));
  }, [query, page]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  }

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Rastreabilidade</span>
          <h1>Auditoria</h1>
          <p>Consultas, autenticações e decisões administrativas.</p>
        </div>
      </div>
      <section className="panel">
        <div className="filters">
          <form className="search-field" onSubmit={submit}>
            <Search size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filtrar por ação"
            />
          </form>
        </div>
        {loading ? (
          <Loading label="A carregar auditoria" />
        ) : items.length === 0 ? (
          <div className="empty-state">
            <Activity size={34} />
            <h3>Sem eventos</h3>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Administrador</th>
                  <th>Ação</th>
                  <th>Recurso</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((event) => (
                  <tr key={event.id}>
                    <td>{dateTime(event.occurredAt)}</td>
                    <td>
                      <strong>{event.admin?.fullName ?? 'Sistema'}</strong>
                      <small className="table-subtitle">{event.admin?.email}</small>
                    </td>
                    <td><code>{event.action}</code></td>
                    <td>{event.resourceType}{event.resourceId ? ` · ${event.resourceId}` : ''}</td>
                    <td><StatusBadge value={event.outcome} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
}
